import React, { useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, MapPinIcon, ClockIcon, UsersIcon, SparklesIcon, MailCheckIcon, GlobeIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { GivebutterDonate } from '../components/GivebutterDonate';
import { VOI_COPY, VOI_IMAGES, ZUNIGA_FILMS, ZUNIGA_SHOWS } from '../data/voices-of-impact-ny-content';
const DETAIL_ICONS = [CalendarIcon, ClockIcon, MapPinIcon, UsersIcon];
const EASE = [0.16, 1, 0.3, 1] as const;
const HAA_LOGO = 'https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png';
function TitleList({
  titles
}: {
  titles: string[];
}) {
  return <>
      {titles.map((title, i) => <React.Fragment key={title} data-id="element-4096" data-mp="39lu7babcp4k0" data-mpi={i}>
          <span className="text-white/90 italic" data-id="element-4097" data-mp="39krkbdh5fm9d">{title}</span>
          {i < titles.length - 1 ? ', ' : ''}
        </React.Fragment>)}
    </>;
}
export function VoicesOfImpactNYPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  const c = isEs ? VOI_COPY.ES : VOI_COPY.EN;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const guests = useMemo(() => [{
    name: 'Neida Sandoval',
    image: VOI_IMAGES.sandoval,
    alt: c.sandovalAlt,
    role: c.sandovalRole,
    bio: <>{c.sandovalBio}</>
  }, {
    name: 'José Zuñiga',
    image: VOI_IMAGES.zuniga,
    alt: c.zunigaAlt,
    role: c.zunigaRole,
    bio: <>
            {c.zunigaBioLead}
            <TitleList titles={ZUNIGA_FILMS} data-id="element-4098" data-mp="3v0lh2ovspecb" />
            {c.zunigaBioMid}
            <TitleList titles={ZUNIGA_SHOWS} data-id="element-4099" data-mp="3v23fga8b506" />
            {c.zunigaBioEnd}
          </>
  }, {
    name: 'Copán Álvarez',
    image: VOI_IMAGES.alvarez,
    alt: c.alvarezAlt,
    role: c.alvarezRole,
    bio: <>{c.alvarezBio}</>
  }], [c]);
  return <div className="min-h-screen w-full bg-[#0b1f3f] text-white font-nunito" data-id="element-4100" data-mp="1684y3hid3x5d">
      <header className="relative z-10 w-full px-6 py-6 sm:px-12 flex items-center justify-between gap-4 border-b border-[#c9a227]/30" data-id="element-4101" data-mp="118ijb3mvy8ug">
        <a href="#" className="flex items-center gap-3 group" aria-label="Honduran American Association" data-id="element-4102" data-mp="w8e4rj5jfo6v">
          <img src={HAA_LOGO} alt="Honduran American Association" width={160} height={40} className="h-10 w-auto object-contain brightness-0 invert group-hover:opacity-80 transition-opacity" data-id="element-4103" data-mp="sa6lohnlqasi" />
        </a>
        <div className="flex items-center gap-3" data-id="element-4104" data-mp="djh5t1z747ib">
          <button onClick={() => setLanguage(isEs ? 'EN' : 'ES')} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a227] hover:bg-[#dcb63b] text-[#0b1f3f] text-sm font-bold transition-colors" aria-label={isEs ? 'Switch to English' : 'Ver en español'} data-id="element-4105" data-mp="8k4jcic8l2g9">
            <GlobeIcon className="w-4 h-4" data-id="element-4106" data-mp="2b5gow47two46" />
            {isEs ? 'English' : 'Español'}
          </button>
          <a href="#" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-sm font-semibold transition-colors" data-id="element-4107" data-mp="2g4jr7gwfcg8g">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-4108" data-mp="2p5rj9h8ckku3" />
            <span className="hidden sm:inline" data-id="element-4109" data-mp="1sgaauuhzckop">{c.back}</span>
          </a>
        </div>
      </header>

      <main data-id="element-4110" data-mp="1wh413uiwabkr">
        {/* Invitation */}
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-16" data-id="element-4111" data-mp="1wi5c5yzwuy2h">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} className="text-center max-w-3xl mx-auto" data-id="element-4112" data-mp="21f5k6fgbmaw8">
            <p className="text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-4" data-id="element-4113" data-mp="18q6zpboq6wnr">
              {c.eyebrow}
            </p>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-[0.08em] leading-[0.95] mb-3" data-id="element-4114" data-mp="3gd9pvyx97fpi">
              Voices of Impact
            </h1>
            <p className="text-white/80 text-sm uppercase tracking-[0.2em]" data-id="element-4115" data-mp="3bclvd39tggqj">
              {c.subtitle}
            </p>
            <div className="w-20 h-px bg-[#c9a227] mx-auto mt-7" data-id="element-4116" data-mp="3kfmycv3dxrpz" />
          </motion.div>

          {/* Info bar */}
          <dl className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10" data-id="element-4117" data-mp="2ydf8uohdif9">
            {c.details.map((item, i) => {
            const Icon = DETAIL_ICONS[i];
            return <div key={item.label} className="bg-[#0b1f3f] px-5 py-5 flex items-start gap-3" data-id="element-4118" data-mp="bys3o2wqrdmz" data-mpi={i}>
                  <Icon className="w-4 h-4 text-[#c9a227] mt-0.5 flex-shrink-0" data-id="element-4119" data-mp="2dqeqrtdiioe6" />
                  <div data-id="element-4120" data-mp="29pmd8nxlxxc3">
                    <dt className="text-white/45 text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-4121" data-mp="29q4g43erz9sz">
                      {item.label}
                    </dt>
                    <dd className="text-white text-sm font-semibold leading-snug" data-id="element-4122" data-mp="23mzuxtz11448">
                      {item.value}
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </section>

        {/* Flyer + RSVP */}
        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-12 lg:py-16" data-id="element-4123" data-mp="1usl6p1wiqj8z">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-stretch" data-id="element-4124" data-mp="1fzjf3qsy71u6">
            <motion.aside initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} className="flex" data-id="element-4125" data-mp="1ky327uja2jo4">
              <a href={VOI_IMAGES.flyer} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#c9a227]/50 bg-white group" aria-label={c.flyerAria} data-id="element-4126" data-mp="1bxxv7lhzxnah">
                <img src={VOI_IMAGES.flyer} alt={c.flyerAlt} decoding="async" className="w-full h-full object-contain block transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-4127" data-mp="2ztm9kyz4fqoe" />
              </a>
            </motion.aside>

            <motion.div initial={{
            opacity: 0,
            y: 24
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1,
            ease: EASE
          }} className="flex flex-col h-full" data-id="element-4128" data-mp="3njoumv4mbwdi">
              <div className="rounded-2xl border border-[#c9a227]/40 bg-white/[0.04] overflow-hidden flex flex-col flex-1" data-id="element-4129" data-mp="3drj6xxleg144">
                <div className="px-6 pt-6 pb-4" data-id="element-4130" data-mp="a4afk6z14ek9">
                  <h2 className="inline-flex items-center gap-2 font-bebas text-2xl tracking-wider mb-1.5" data-id="element-4131" data-mp="a52cpm89ykcn">
                    <MailCheckIcon className="w-5 h-5 text-[#c9a227]" data-id="element-4132" data-mp="5bzgf6c44wsr" />
                    {c.rsvpTitle}
                  </h2>
                  <p className="text-white/65 text-sm leading-relaxed" data-id="element-4133" data-mp="cr31z9c2o4xt">
                    {c.rsvpText}
                  </p>
                </div>
                <div className="bg-white p-4 sm:p-5 flex-1 flex flex-col justify-center" data-id="element-4134" data-mp="mmmsfg7vabf6">
                  <GivebutterDonate data-id="element-4135" data-mp="rm99k0qtgbt9" />
                  {React.createElement('givebutter-widget', {
                  id: 'LylV7o'
                })}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Special guests */}
        <section className="border-t border-white/10" data-id="element-4136" data-mp="10kuqjrgn3irx">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20" data-id="element-4137" data-mp="15d4b0ryzjqmt">
            <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-4138" data-mp="1ac7dc4nkzir3">
              <p className="inline-flex items-center gap-2 text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4139" data-mp="1aejska1yff0g">
                <SparklesIcon className="w-4 h-4" data-id="element-4140" data-mp="1jcvsj2opnp5k" />
                {c.guestsEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4141" data-mp="24nkmxfpw6ghp">
                {c.guestsHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-7" data-id="element-4142" data-mp="20m8tez3wdb48" />
              <p className="text-white/80 text-base sm:text-lg leading-relaxed" data-id="element-4143" data-mp="2el8i26retq0e">
                {c.guestsIntro}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10" data-id="element-4144" data-mp="29ldr637mx0fc">
              {guests.map((guest, i) => <motion.article key={guest.name} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-80px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08,
              ease: EASE
            }} className="flex flex-col" data-id="element-4145" data-mp="1gyj6p6ati11v" data-mpi={i}>
                  <div className="rounded-2xl overflow-hidden border border-[#c9a227]/40 shadow-2xl bg-white" data-id="element-4146" data-mp="1351qnvhcb6gl">
                    <img src={guest.image} alt={guest.alt} loading="lazy" decoding="async" className="w-full h-auto object-contain block" data-id="element-4147" data-mp="y677gsfgnfvy" />
                  </div>
                  <div className="pt-7" data-id="element-4148" data-mp="30sm4ancaf60b">
                    <h3 className="font-bebas text-3xl tracking-wider mb-1.5" data-id="element-4149" data-mp="34vipmlt7pg7y">
                      {guest.name}
                    </h3>
                    <p className="text-[#c9a227] text-[11px] font-bold uppercase tracking-[0.18em] mb-4" data-id="element-4150" data-mp="3smo34ubojwf3">
                      {guest.role}
                    </p>
                    <p className="text-white/70 text-sm leading-relaxed" data-id="element-4151" data-mp="3jlzs88hl2c6f">
                      {guest.bio}
                    </p>
                  </div>
                </motion.article>)}
            </div>
          </div>
        </section>

        {/* About the evening — New York backdrop */}
        <section className="relative border-t border-white/10 overflow-hidden" data-id="element-4152" data-mp="2d65qb41fb8ir">
          <img src={VOI_IMAGES.backdrop} alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" data-id="element-4153" data-mp="34c606dzk0zi0" />
          <div className="absolute inset-0 bg-[#0b1f3f]/55" data-id="element-4154" data-mp="2umcv7vtx3imq" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b1f3f] via-[#0b1f3f]/20 to-[#0b1f3f]" data-id="element-4155" data-mp="2zl61b36vk7cv" />

          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-24" data-id="element-4156" data-mp="2knnew7ho668l">
            <div className="text-center max-w-3xl mx-auto" data-id="element-4157" data-mp="2po1nbqn6ni75">
              <p className="inline-flex items-center gap-2 text-[#c9a227] text-xs font-bold uppercase tracking-[0.25em] mb-5" data-id="element-4158" data-mp="2plf7z4u1vyed">
                <SparklesIcon className="w-4 h-4" data-id="element-4159" data-mp="2gmldpl46zk3z" />
                {c.aboutEyebrow}
              </p>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-wider leading-tight mb-4" data-id="element-4160" data-mp="9w3yu37h7agj">
                {c.aboutHeading}
              </h2>
              <div className="w-16 h-px bg-[#c9a227] mx-auto mb-8" data-id="element-4161" data-mp="euyrrxz5qfed" />
            </div>
            <div className="space-y-5 text-white/80 text-base sm:text-lg leading-relaxed text-center max-w-3xl mx-auto" data-id="element-4162" data-mp="p06r53sym3s3">
              {c.aboutParagraphs.map(text => <p key={text} data-id="element-4163" data-mp="xt0nyf4g9ltp">{text}</p>)}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-14" data-id="element-4164" data-mp="t1iknkeyf95k">
              {c.highlights.map((item, i) => <motion.div key={item.title} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: i * 0.08,
              ease: EASE
            }} className="rounded-2xl border border-white/15 bg-[#0b1f3f]/70 p-7 text-center" data-id="element-4165" data-mp="17pn4irpqtqpx" data-mpi={i}>
                  <div className="w-10 h-px bg-[#c9a227] mb-5 mx-auto" data-id="element-4166" data-mp="3iizgphcxbobq" />
                  <h3 className="font-bebas text-2xl tracking-wider mb-3" data-id="element-4167" data-mp="3eh4fecuztaxy">
                    {item.title}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed" data-id="element-4168" data-mp="1hh9z6v4a04qz">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="border-t border-white/10 bg-white/[0.03]" data-id="element-4169" data-mp="1wd96i3jka0v2">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center" data-id="element-4170" data-mp="1wekbu7snngoa">
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight mb-5" data-id="element-4171" data-mp="20e2tmup6jbmw">
              {c.missionHeading}
            </h2>
            <p className="text-white/75 text-base sm:text-lg leading-relaxed mb-4" data-id="element-4172" data-mp="25bkxi6ocb0o1">
              {c.missionText}
            </p>
            <p className="text-white text-base sm:text-lg font-semibold leading-relaxed" data-id="element-4173" data-mp="2f40byu0fh746">
              {c.missionClosing}
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10 py-8 text-center" data-id="element-4174" data-mp="2o45iz31pm3ht">
        <p className="text-white/45 text-xs uppercase tracking-[0.2em]" data-id="element-4175" data-mp="2o2cdvs6qvrbx">
          {c.footer}
        </p>
      </footer>
    </div>;
}